import {
  PreScreeningQuestion,
  QuestionType,
  standardHeightQuestion,
  standardWeightQuestion,
} from "../../types/landingPage";
import { mappers } from "../../utils/attributeMapping/mappers";
import {
  MEASUREMENT_ATTRIBUTES,
  CARDIOVASCULAR_ATTRIBUTES,
  CLINICAL_TRIAL_ATTRIBUTES,
  RESPIRATORY_ATTRIBUTES,
  DIABETES_ATTRIBUTES,
  LIVER_KIDNEY_ATTRIBUTES,
  INFECTIOUS_DISEASE_ATTRIBUTES,
} from "../../utils/attributeMapping/attributeConstants";

/**
 * Pre-screening questions for Cidara 18+ High Risk Flu Prevention Study
 * Target population: Adults 18+ at higher risk for flu complications with weight >40kg and BMI >18 kg/m²
 *
 * Key inclusion: Must have at least ONE high-risk condition from the extensive OR list
 */
export const cidara18HighRiskScreeningQuestions: PreScreeningQuestion[] = [
  {
    id: "age_eligibility",
    type: QuestionType.RADIO,
    question: "What is your age?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "17_or_under",
        label: "17 or under",
        disqualify: true
      },
      {
        value: "18_to_49",
        label: "18 to 49"
      },
      {
        value: "50_to_64",
        label: "50 to 64"
      },
      {
        value: "65_or_older",
        label: "65 or older"
      },
    ],
    disqualifyMessage: "This study is for adults aged 18 and older.",
    attributeMapping: mappers.value({
      "17_or_under": [],
      "18_to_49": [],
      "50_to_64": [],
      "65_or_older": [],
    }),
  },

  // High-risk conditions check - this is the critical inclusion criterion
  {
    id: "high_risk_conditions",
    type: QuestionType.CHECKBOX,
    question: "Do you have any of the following health conditions?",
    helpText: "This study is for people at higher risk for flu complications. Please select all that apply.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "copd",
        label: "COPD (Chronic Obstructive Pulmonary Disease)"
      },
      {
        value: "asthma_with_preventer",
        label: "Asthma and I use a preventer inhaler daily"
      },
      {
        value: "cystic_fibrosis",
        label: "Cystic fibrosis"
      },
      {
        value: "lung_fibrosis_ild",
        label: "Lung fibrosis or interstitial lung disease"
      },
      {
        value: "heart_failure",
        label: "Heart failure"
      },
      {
        value: "congenital_heart_disease",
        label: "Congenital heart disease (heart condition present from birth)"
      },
      {
        value: "coronary_heart_disease",
        label: "Coronary heart disease or history of heart attack"
      },
      {
        value: "diabetes_on_insulin",
        label: "Diabetes and I take insulin"
      },
      {
        value: "chronic_kidney_disease",
        label: "Chronic kidney disease"
      },
      {
        value: "immunosuppressant_medications",
        label: "I take immunosuppressing medications (such as prednisone, methotrexate, azathioprine, cyclosporine, mycophenolate, or rituximab)"
      },
      {
        value: "hiv_diagnosed",
        label: "HIV (diagnosed and aware of status)"
      },
      {
        value: "immunodeficiency",
        label: "Primary immunodeficiency or immunocompromised condition"
      },
      {
        value: "blood_cancer",
        label: "Blood cancer (such as leukemia, lymphoma, or multiple myeloma)"
      },
      {
        value: "b_cell_depleting_meds",
        label: "I have taken B-cell depleting medications (such as rituximab, ocrelizumab, or ofatumumab) in the past 12 months"
      },
      {
        value: "none",
        label: "None of the above conditions apply to me",
        disqualify: true
      },
    ],
    disqualifyMessage: "This study is specifically for adults at higher risk for flu complications. You must have at least one of the listed high-risk conditions to participate.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        copd: [
          { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_COPD, booleanAttributeValue: true },
        ],
        asthma_with_preventer: [
          { attributeDefinitionId: RESPIRATORY_ATTRIBUTES.HAS_ASTHMA, booleanAttributeValue: true },
        ],
        cystic_fibrosis: [
          { attributeDefinitionId: 20354, booleanAttributeValue: true }, // has_CF
        ],
        lung_fibrosis_ild: [
          { attributeDefinitionId: 23497, booleanAttributeValue: true }, // has_chronic_lung_disease (covers ILD)
        ],
        heart_failure: [
          { attributeDefinitionId: CARDIOVASCULAR_ATTRIBUTES.HAS_CONGESTIVE_HEART_FAILURE, booleanAttributeValue: true },
        ],
        congenital_heart_disease: [
          { attributeDefinitionId: 3436, booleanAttributeValue: true }, // has_coronaryarterydisease (covers congenital)
        ],
        coronary_heart_disease: [
          { attributeDefinitionId: 3436, booleanAttributeValue: true }, // has_coronaryarterydisease
        ],
        diabetes_on_insulin: [
          { attributeDefinitionId: DIABETES_ATTRIBUTES.HAS_DIABETES, booleanAttributeValue: true },
          { attributeDefinitionId: 23574, booleanAttributeValue: true }, // has_insulin_daily
        ],
        chronic_kidney_disease: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_KIDNEY_DISEASE, booleanAttributeValue: true },
        ],
        immunosuppressant_medications: [
          { attributeDefinitionId: 3052, booleanAttributeValue: true }, // is_immunocompromised (covers taking immunosuppressants)
        ],
        hiv_diagnosed: [
          { attributeDefinitionId: INFECTIOUS_DISEASE_ATTRIBUTES.HAS_HIV, booleanAttributeValue: true },
        ],
        immunodeficiency: [
          { attributeDefinitionId: 3052, booleanAttributeValue: true }, // is_immunocompromised
        ],
        blood_cancer: [
          { attributeDefinitionId: 23423, booleanAttributeValue: true }, // has_haematological_malignancy
        ],
        b_cell_depleting_meds: [
          { attributeDefinitionId: 3052, booleanAttributeValue: true }, // is_immunocompromised (flag for B-cell depleting meds effect)
        ],
        none: [],
      },
      noneOfAboveValue: "none",
    }),
  },

  {
    id: "recent_influenza_diagnosis",
    type: QuestionType.RADIO,
    question: "Have you been diagnosed with influenza by a medical professional within the past 5 months?",
    helpText: "This refers to a confirmed diagnosis of influenza (flu) by a doctor or healthcare provider in the past 5 months (since May 2025).",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have been diagnosed with influenza within the past 5 months",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not been diagnosed with influenza within the past 5 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have been diagnosed with influenza in the past 5 months cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 23501, booleanAttributeValue: true }, // had_influenza_5_months
      ],
      no: [
        { attributeDefinitionId: 23501, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "exclusionary_conditions",
    type: QuestionType.CHECKBOX,
    question: "Are any of the following true for you?",
    helpText: "Please select all that apply. If none of these apply to you, select 'None of the above'.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "pregnant_breastfeeding",
        label: "I am pregnant or breastfeeding",
        disqualify: true
      },
      {
        value: "major_surgery",
        label: "I have had major surgery in the past month",
        disqualify: true
      },
      {
        value: "kidney_failure_dialysis",
        label: "I am on dialysis for end stage kidney disease",
        disqualify: true
      },
      {
        value: "bleeding_disorder",
        label: "I have a bleeding disorder",
        disqualify: true
      },
      {
        value: "blood_donation",
        label: "I have donated blood within the past month",
        disqualify: true
      },
      {
        value: "none",
        label: "None of the above"
      },
    ],
    disqualifyMessage: "For safety reasons, this study cannot include participants with these conditions or circumstances.",
    attributeMapping: mappers.multiSelect({
      valueMap: {
        pregnant_breastfeeding: [
          { attributeDefinitionId: 26453, booleanAttributeValue: true }, // is_pregnant_prerequisite
          { attributeDefinitionId: 26455, booleanAttributeValue: true }, // is_breastfeeding_prerequisite
        ],
        major_surgery: [
          { attributeDefinitionId: 15603, booleanAttributeValue: true }, // had_majorsurgery_1month
        ],
        kidney_failure_dialysis: [
          { attributeDefinitionId: LIVER_KIDNEY_ATTRIBUTES.HAS_END_STAGE_RENAL_DISEASE, booleanAttributeValue: true },
          { attributeDefinitionId: 23565, booleanAttributeValue: true }, // has_dialysis_current
        ],
        bleeding_disorder: [
          { attributeDefinitionId: 15061, booleanAttributeValue: true }, // has_bleedingdisorder
        ],
        blood_donation: [
          { attributeDefinitionId: 23728, booleanAttributeValue: true }, // has_donated_blood_1_month
        ],
        none: [],
      },
      noneOfAboveValue: "none",
    }),
  },

  {
    id: "ivig_treatment",
    type: QuestionType.RADIO,
    question: "Have you received intravenous immunoglobulin (IVIg) treatment in the past month?",
    helpText: "IVIg is an infusion therapy given through a vein, often used to treat immune system problems.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "yes",
        label: "Yes, I have received IVIg treatment in the past month",
        disqualify: true
      },
      {
        value: "no",
        label: "No, I have not received IVIg treatment in the past month"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received IVIg treatment in the past month cannot participate in this study.",
    attributeMapping: mappers.value({
      yes: [
        { attributeDefinitionId: 21642, medicationAttributeValue: { hasTaken: true, intervals: [] } },
      ],
      no: [
        { attributeDefinitionId: 21642, medicationAttributeValue: { hasTaken: false, intervals: [] } },
      ],
    }),
  },

  {
    ...standardHeightQuestion,
    attributeMapping: mappers.height({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.HEIGHT }),
  },
  {
    ...standardWeightQuestion,
    minWeight: 89, // 40kg = 88.18 lbs, rounded up to 89
    minBMI: 18,
    attributeMapping: mappers.weight({ attributeDefinitionId: MEASUREMENT_ATTRIBUTES.WEIGHT }),
  },

  {
    id: "concurrent_trial",
    type: QuestionType.RADIO,
    question: "Are you currently enrolling in or participating in another clinical trial?",
    required: true,
    allowBack: true,
    options: [
      {
        value: "no",
        label: "No, I am not currently enrolling in or participating in another clinical trial"
      },
      {
        value: "yes_recent_medication",
        label: "Yes, I am currently participating in another clinical trial and I have received clinical trial medication in the past 3 months",
        disqualify: true
      },
      {
        value: "yes_follow_up_only",
        label: "Yes, I am participating in the follow-up of a clinical trial but haven't received any trial medication in the past 3 months"
      },
    ],
    disqualifyMessage: "For safety reasons, participants who have received clinical trial medication in the past 3 months cannot participate in this study.",
    attributeMapping: mappers.value({
      no: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
      yes_recent_medication: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: true },
      ],
      yes_follow_up_only: [
        { attributeDefinitionId: CLINICAL_TRIAL_ATTRIBUTES.IN_OTHER_STUDY, booleanAttributeValue: false },
      ],
    }),
  },

  {
    id: "past_vaccine_study_participation",
    type: QuestionType.RADIO,
    question: "Have you ever participated in a vaccine study before?",
    helpText: "This information helps us understand your experience with vaccine research.",
    required: true,
    allowBack: true,
    options: [
      {
        value: "participated",
        label: "Yes, I have participated in a vaccine study before"
      },
      {
        value: "screened_not_enrolled",
        label: "I have screened for a vaccine study but didn't enroll"
      },
      {
        value: "never_participated",
        label: "No, I have never participated in a vaccine study"
      },
    ],
    attributeMapping: mappers.value({
      participated: [],
      screened_not_enrolled: [],
      never_participated: [],
    }),
  },
];
